import { useState, useEffect } from "react";
import { useAppFilters } from "../state/filters";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Calendar as CalendarIcon, Check } from "lucide-react";

const PRESETS = [
  { label: "Today", today: true },
  { label: "Yesterday", yesterday: true },
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
  { label: "Last 12 months", days: 365 },
  { label: "All time", days: 3650 },
  { label: "Month to date", mtd: true },
  { label: "Quarter to date", qtd: true },
  { label: "Year to date", ytd: true },
];

function makeRange(p) {
  const now = new Date();
  const end = new Date(now);
  const start = new Date(now);
  if (p.today) {
    start.setHours(0, 0, 0, 0);
  } else if (p.yesterday) {
    start.setDate(start.getDate() - 1);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
  } else if (p.days) {
    start.setDate(start.getDate() - p.days);
    start.setHours(0, 0, 0, 0);
  } else if (p.mtd) {
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
  } else if (p.qtd) {
    const q = Math.floor(start.getMonth() / 3) * 3;
    start.setMonth(q, 1);
    start.setHours(0, 0, 0, 0);
  } else if (p.ytd) {
    start.setMonth(0, 1);
    start.setHours(0, 0, 0, 0);
  }
  return { start: start.toISOString(), end: end.toISOString(), label: p.label };
}

function fmtDisplay(d) {
  return d
    ? d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : "—";
}

function toLocalDate(iso) {
  if (!iso) return undefined;
  try {
    return new Date(iso);
  } catch {
    return undefined;
  }
}

export default function DateRangePicker() {
  const { range, setRange } = useAppFilters();
  const [open, setOpen] = useState(false);

  // Local range while the user is selecting in the calendar
  const [dateRange, setDateRange] = useState({
    from: toLocalDate(range?.start),
    to: toLocalDate(range?.end),
  });
  const [activeLabel, setActiveLabel] = useState(range?.label || "Last 30 days");

  useEffect(() => {
    setDateRange({ from: toLocalDate(range?.start), to: toLocalDate(range?.end) });
    setActiveLabel(range?.label || "Custom");
  }, [range?.start, range?.end, range?.label]);

  const choosePreset = (p) => {
    const r = makeRange(p);
    setRange(r);
    setDateRange({ from: new Date(r.start), to: new Date(r.end) });
    setActiveLabel(r.label);
    setOpen(false);
  };

  const applyCustom = () => {
    if (!dateRange?.from) return;
    const from = new Date(dateRange.from);
    from.setHours(0, 0, 0, 0);
    const to = dateRange.to ? new Date(dateRange.to) : new Date(dateRange.from);
    to.setHours(23, 59, 59, 999);
    const label = `${fmtDisplay(from)} – ${fmtDisplay(to)}`;
    setRange({ start: from.toISOString(), end: to.toISOString(), label });
    setActiveLabel(label);
    setOpen(false);
  };

  const reset = () => {
    setDateRange({ from: toLocalDate(range?.start), to: toLocalDate(range?.end) });
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) reset(); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="global-date-range-picker" className="gap-2">
          <CalendarIcon size={14} />
          <span className="truncate max-w-[260px]">{activeLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-auto p-0 overflow-hidden bg-[hsl(var(--surface-1))] border-border shadow-2xl"
        data-testid="date-range-panel"
      >
        <div className="flex flex-col md:flex-row">
          {/* Preset sidebar */}
          <div className="w-full md:w-52 border-b md:border-b-0 md:border-r border-border p-2 bg-[hsl(var(--surface-2))]/40">
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground px-2 py-2">
              Presets
            </div>
            <div className="flex flex-col">
              {PRESETS.map((p) => {
                const active = activeLabel === p.label;
                return (
                  <button
                    key={p.label}
                    onClick={() => choosePreset(p)}
                    className={`text-left text-sm px-3 py-2 rounded-md transition flex items-center justify-between ${
                      active
                        ? "bg-[hsl(var(--surface-3))] text-foreground"
                        : "text-muted-foreground hover:bg-[hsl(var(--surface-3))]/60 hover:text-foreground"
                    }`}
                    data-testid={`date-range-preset-${p.label.toLowerCase().replaceAll(" ", "-")}`}
                  >
                    {p.label}
                    {active && <Check size={14} className="text-[hsl(var(--positive))]" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Calendar + custom range */}
          <div className="flex flex-col">
            <div className="p-2">
              <Calendar
                mode="range"
                numberOfMonths={2}
                selected={dateRange}
                onSelect={setDateRange}
                defaultMonth={dateRange?.from || new Date()}
                data-testid="date-range-calendar"
              />
            </div>
            <div className="flex items-center justify-between border-t border-border px-3 py-2 bg-[hsl(var(--surface-2))]/40">
              <div className="text-xs text-muted-foreground tabular-nums">
                <span className="text-foreground">{fmtDisplay(dateRange?.from)}</span>
                <span className="mx-2">→</span>
                <span className="text-foreground">{fmtDisplay(dateRange?.to)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setOpen(false)} data-testid="date-range-cancel">
                  Cancel
                </Button>
                <Button size="sm" onClick={applyCustom} disabled={!dateRange?.from} data-testid="date-range-apply">
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
